import { ChannelType } from 'components/CampaignCreationModal/Types'
import { LandingPageSchema } from './LandingPage'
import { NewsletterSchema } from './Newsletter'
import { SMSMessagesSchema } from './SMSMessages'
import { FacebookAdsSchema } from './FacebookAds'
import { GoogleAdsSchema } from './GoogleAds'
import { TwitterThreadSchema } from './TwitterThread'
import { TweetsSchema } from './Tweets'
import { InstagramPostsSchema } from './InstagramPosts'
import { LinkedInPostsSchema } from './LinkedInPosts'
import { PressReleaseSchema } from './PressRelease'
import { TikTokScriptSchema } from './TikTokScript'
import { FAQSchema } from './FAQs'
import { FormSchema as BaseFormSchema } from '../Wizard/Form/Types'
import { ContentBriefSchema } from './ContentBrief'
import { OutlineSchema } from './Outline'
import { BrainstormSchema } from './Brainstorm'
import { BaseSchema } from './Base'
import {
  AbandonedCart,
  AboutUs,
  BattleCard,
  BillingAndPayments,
  Blog,
  CallScript,
  CandidateNurture,
  Care,
  Cold,
  DidntSignUp,
  HelpDocs,
  Impact,
  JobDescription,
  LostCustomer,
  Mission,
  OnePager,
  PaymentConfirmation,
  PreCallChecklist,
  Pricing,
  ProductDemo,
  ReplyTemplates,
  Research,
  ReturnPolicy,
  ShippingPolicy,
  Testimonial,
  Warm,
  WelcomeSeries,
} from 'almanac-ui/icons/blaze/32'
import {
  Amazon,
  Bing,
  Depop,
  Design,
  Ebay,
  Etsy,
  Facebook,
  Google,
  Instagram,
  Lazada,
  LinkedIn,
  Mercadolibre,
  Mercari,
  Pinterest,
  Poshmark,
  Shopee,
  Shopify,
  SquareSpace,
  TikTok,
  Twitter,
  Vinted,
  WhatsApp,
  YouTube,
} from 'blaze-ui/icons'
import { MetaBrand } from 'blaze-ui/icons/20/MetaBrand'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { object } from 'yup'
import { topic } from './shared/topic'
import { YoutubeScriptSchema } from './YoutubeScript'
import { InstagramReelsSchema } from './InstagramReels'
import { NurtureEmailSequenceSchema } from './NurtureEmailSequence'
import { PromotionalEmailSequenceSchema } from './PromotionalEmailSequence'
import { CaseStudySchema } from './CaseStudy'
import { seoWords } from './shared/seoWords'
import { FacebookPostsSchema } from './FacebookPosts'
import { ImageSchema } from './Image'
import { BlogPostSchema } from 'components/Wizards/schemas/BlogPost'
import { SEOPlanningSchema } from 'components/Wizards/schemas/SEOPlanning'
import { InstagramPlanningSchema } from 'components/Wizards/schemas/InstagramPlanning'
import { CampaignSchema } from 'components/Wizards/schemas/Campaign'
import { includeImages } from './shared/includeImages'
import { TwitterPlanningSchema } from './TwitterPlanning'
import { FacebookPlanningSchema } from './FacebookPlanning'
import { LinkedInPlanningSchema } from './LinkedInPlanning'
import { DesignChannel } from 'components/NewFileModal/Types'
import { MultiStepSchema } from './Types'
import {
  BaseDesignSchemaWithoutCaption,
  BaseMarketplaceDesignSchema,
  BaseVideoDesignSchema,
  getBaseDesignSchemaWithCaption,
} from './BaseDesign'
import { ContentPlanSchema } from './ContentPlanSchema'
import { TextChannels } from '../../../constants'
import { Brainstorm } from 'blaze-ui/icons/24'

type FormSchema = BaseFormSchema<GenerationType | ChannelType>

export const schemas: { [key in ChannelType | GenerationType | DesignChannel.GoogleMyBusinessPost]: FormSchema } = {
  [ChannelType.FacebookAds]: FacebookAdsSchema,
  [ChannelType.FacebookPost]: FacebookPostsSchema,
  [ChannelType.FAQs]: FAQSchema,
  [ChannelType.GoogleAds]: GoogleAdsSchema,
  [DesignChannel.GoogleMyBusinessPost]: BaseSchema.meta({
    type: DesignChannel.GoogleMyBusinessPost,
    icon: Google,
    title: TextChannels[DesignChannel.GoogleMyBusinessPost].name,
    previewUrl: '',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What would you like to post on your Google Business Profile?',
        generationType: DesignChannel.GoogleMyBusinessPost,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.InstagramPosts]: InstagramPostsSchema,
  [ChannelType.InstagramCaptions]: InstagramPostsSchema,
  [ChannelType.InstagramReel]: InstagramReelsSchema,
  [ChannelType.LandingPage]: LandingPageSchema,
  [ChannelType.LinkedInPosts]: LinkedInPostsSchema,
  [ChannelType.Newsletter]: NewsletterSchema,
  [ChannelType.PressRelease]: PressReleaseSchema,
  [ChannelType.SMSMessages]: SMSMessagesSchema,
  [ChannelType.TikTokScript]: TikTokScriptSchema,
  [ChannelType.TweetThread]: TwitterThreadSchema,
  [ChannelType.Tweets]: TweetsSchema,
  [GenerationType.ContentBrief]: ContentBriefSchema,
  [GenerationType.Outline]: OutlineSchema,
  [GenerationType.ContentPlan]: ContentPlanSchema,
  [GenerationType.Brainstorm]: BrainstormSchema,
  [GenerationType.Campaign]: CampaignSchema,
  [GenerationType.SEOPlanning]: SEOPlanningSchema,
  [GenerationType.InstagramPlanning]: InstagramPlanningSchema,
  [GenerationType.TwitterPlanning]: TwitterPlanningSchema,
  [GenerationType.FacebookPlanning]: FacebookPlanningSchema,
  [GenerationType.LinkedInPlanning]: LinkedInPlanningSchema,
  [ChannelType.AboutUs]: BaseSchema.meta({
    type: ChannelType.AboutUs,
    icon: AboutUs,
    title: TextChannels[ChannelType.AboutUs].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183780/Home%20Card%20Previews/orzqa5ssnrd80byzzaws.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What would you like to convey in your "About Us" section?',
        generationType: ChannelType.AboutUs,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.Pricing]: BaseSchema.meta({
    type: ChannelType.Pricing,
    icon: Pricing,
    title: TextChannels[ChannelType.Pricing].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183789/Home%20Card%20Previews/yyrbytkw6ocv9w07ni5z.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What are the key details of your pricing strategy or tiers?',
        generationType: ChannelType.Pricing,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.Mission]: BaseSchema.meta({
    type: ChannelType.Mission,
    icon: Mission,
    title: TextChannels[ChannelType.Mission].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183789/Home%20Card%20Previews/a6tzhejlo1zyunzwmmik.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What is the mission of your company or initiative?',
        generationType: ChannelType.Mission,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.Impact]: BaseSchema.meta({
    type: ChannelType.Impact,
    icon: Impact,
    title: TextChannels[ChannelType.Impact].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183788/Home%20Card%20Previews/nhxcgolpv2skyxb82okv.png',
  }).concat(
    object({
      topic: topic.meta({ label: 'What is the impact your company will make?', generationType: ChannelType.Impact }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.JobDescription]: BaseSchema.meta({
    type: ChannelType.JobDescription,
    icon: JobDescription,
    title: TextChannels[ChannelType.JobDescription].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183788/Home%20Card%20Previews/d4gaqor2xhz5fspuxtx3.png',
  }).concat(
    object({
      topic: topic.meta({ label: 'Describe the role and qualifications', generationType: ChannelType.JobDescription }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.CandidateNurtureSequence]: BaseSchema.meta({
    type: ChannelType.CandidateNurtureSequence,
    icon: CandidateNurture,
    title: TextChannels[ChannelType.CandidateNurtureSequence].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183781/Home%20Card%20Previews/qqvovuiexnviysjuzdfb.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What is the role and company context for your email sequence?',
        generationType: ChannelType.CandidateNurtureSequence,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.ProductLaunchWebsite]: BaseSchema.meta({
    type: ChannelType.ProductLaunchWebsite,
    icon: SquareSpace,
    title: TextChannels[ChannelType.ProductLaunchWebsite].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183792/Home%20Card%20Previews/lcc0d2jbmt8qpkpucaiy.png',
  }).concat(
    object({
      topic: topic.meta({
        label: "What's the key message for your product launch website?",
        generationType: ChannelType.ProductLaunchWebsite,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.ProductLaunchAmazon]: BaseSchema.meta({
    type: ChannelType.ProductLaunchAmazon,
    icon: Amazon,
    title: TextChannels[ChannelType.ProductLaunchAmazon].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183780/Home%20Card%20Previews/bnu7hxjcaid80sgn2ad1.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What are the details of your product launching on Amazon?',
        generationType: ChannelType.ProductLaunchAmazon,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.ProductLaunchShopify]: BaseSchema.meta({
    type: ChannelType.ProductLaunchShopify,
    icon: Shopify,
    title: TextChannels[ChannelType.ProductLaunchShopify].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183790/Home%20Card%20Previews/g38svh6hhvqvlu6ecgyj.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What are the details of your product launching on Shopify?',
        generationType: ChannelType.ProductLaunchShopify,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.ProductLaunchEtsy]: BaseSchema.meta({
    type: ChannelType.ProductLaunchEtsy,
    icon: Etsy,
    title: TextChannels[ChannelType.ProductLaunchEtsy].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183782/Home%20Card%20Previews/oxezvsypalhssdkbcrwg.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What are the details of your product launching on Etsy?',
        generationType: ChannelType.ProductLaunchEtsy,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.ProductLaunchGoogle]: BaseSchema.meta({
    type: ChannelType.ProductLaunchGoogle,
    icon: Google,
    title: TextChannels[ChannelType.ProductLaunchGoogle].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183787/Home%20Card%20Previews/pvcghvmua60xvlw7jwjt.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What are the details of your product launching on Google?',
        generationType: ChannelType.ProductLaunchGoogle,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.BlogIdeas]: BaseSchema.meta({
    type: ChannelType.BlogIdeas,
    icon: Brainstorm,
    title: TextChannels[ChannelType.BlogIdeas].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183780/Home%20Card%20Previews/g2fmsqafmcc6vmfmmf2r.png',
  }).concat(
    object({
      topic: topic.meta({ label: 'What is the topic for your blog ideas?', generationType: ChannelType.BlogIdeas }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.ColdOutboundEmailSequence]: BaseSchema.meta({
    type: ChannelType.ColdOutboundEmailSequence,
    icon: Cold,
    title: TextChannels[ChannelType.ColdOutboundEmailSequence].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183781/Home%20Card%20Previews/iicaenxh1pr9rvb06vfn.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What is the objective and audience for your cold outbound email sequence?',
        generationType: ChannelType.ColdOutboundEmailSequence,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.WarmOutboundEmailSequence]: BaseSchema.meta({
    type: ChannelType.WarmOutboundEmailSequence,
    icon: Warm,
    title: TextChannels[ChannelType.WarmOutboundEmailSequence].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183791/Home%20Card%20Previews/nhwwvp0qwzog9qvwnbvk.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What is the objective and audience for your warm outbound email sequence?',
        generationType: ChannelType.WarmOutboundEmailSequence,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.AmazonAds]: BaseSchema.meta({
    type: ChannelType.AmazonAds,
    icon: Amazon,
    title: TextChannels[ChannelType.AmazonAds].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183780/Home%20Card%20Previews/wb1ik4gohau3wqh5qiif.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What is the goal of your Amazon Sponsored Brand ad?',
        generationType: ChannelType.AmazonAds,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.BingAds]: BaseSchema.meta({
    type: ChannelType.BingAds,
    icon: Bing,
    title: TextChannels[ChannelType.BingAds].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183780/Home%20Card%20Previews/ji9brlewqi6zg1aot28c.png',
  }).concat(
    object({
      topic: topic.meta({ label: 'What are the goals of your Bing ad campaign?', generationType: ChannelType.BingAds }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.LinkedInAds]: BaseSchema.meta({
    type: ChannelType.LinkedInAds,
    icon: LinkedIn,
    title: TextChannels[ChannelType.LinkedInAds].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183788/Home%20Card%20Previews/azftrk6yx0dj1icezgz5.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What are the goals of your LinkedIn ad campaign?',
        generationType: ChannelType.LinkedInAds,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.PinterestAds]: BaseSchema.meta({
    type: ChannelType.PinterestAds,
    icon: Pinterest,
    title: TextChannels[ChannelType.PinterestAds].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183789/Home%20Card%20Previews/jrg2gpftmkzwnfjy2mg4.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What are the goals of your Pinterest ad campaign?',
        generationType: ChannelType.PinterestAds,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.TikTokAds]: BaseSchema.meta({
    type: ChannelType.TikTokAds,
    icon: TikTok,
    title: TextChannels[ChannelType.TikTokAds].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183791/Home%20Card%20Previews/acm5p0sjtzfdmauwilvw.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What are the goals of your TikTok video ad?',
        generationType: ChannelType.TikTokAds,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.YoutubeAds]: BaseSchema.meta({
    type: ChannelType.YoutubeAds,
    icon: YouTube,
    title: TextChannels[ChannelType.YoutubeAds].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183792/Home%20Card%20Previews/speaqonwubpvqpfovnyc.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What are the goals of your YouTube ad campaign?',
        generationType: ChannelType.YoutubeAds,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.NewsletterIdeas]: BaseSchema.meta({
    type: ChannelType.NewsletterIdeas,
    icon: Brainstorm,
    title: TextChannels[ChannelType.NewsletterIdeas].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183792/Home%20Card%20Previews/sin5rn5hienbrts9655v.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What is the topic for your newsletter ideas?',
        generationType: ChannelType.NewsletterIdeas,
      }),
    }),
  ) as FormSchema,
  [ChannelType.WelcomeSeries]: BaseSchema.meta({
    type: ChannelType.WelcomeSeries,
    icon: WelcomeSeries,
    title: TextChannels[ChannelType.WelcomeSeries].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183791/Home%20Card%20Previews/e64wuwzbzuuhs9zbbnp8.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What do you want to communicate in your welcome series?',
        generationType: ChannelType.WelcomeSeries,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.DidntSignUp]: BaseSchema.meta({
    type: ChannelType.DidntSignUp,
    icon: DidntSignUp,
    title: TextChannels[ChannelType.DidntSignUp].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183781/Home%20Card%20Previews/uhqjlhbkm3urlpggc0yl.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'Describe the details of your product and any key notes for users who did not sign up.',
        generationType: ChannelType.DidntSignUp,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.AbandonedCart]: BaseSchema.meta({
    type: ChannelType.AbandonedCart,
    icon: AbandonedCart,
    title: TextChannels[ChannelType.AbandonedCart].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183779/Home%20Card%20Previews/avfbfp1lq3iserokb9wm.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'Describe the details of your product and any key notes for users who abandoned cart.',
        generationType: ChannelType.AbandonedCart,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.LostCustomer]: BaseSchema.meta({
    type: ChannelType.LostCustomer,
    icon: LostCustomer,
    title: TextChannels[ChannelType.LostCustomer].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183788/Home%20Card%20Previews/ea3hzlr576sr0nezoxx8.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'Describe the details of your product and how you would like to re-engage lost customers.',
        generationType: ChannelType.LostCustomer,
      }),
      include_images: includeImages,
      seo_words: seoWords.meta({ inCollapsibleSection: true }),
    }),
  ) as FormSchema,
  [ChannelType.OnePager]: BaseSchema.meta({
    type: ChannelType.OnePager,
    icon: OnePager,
    title: TextChannels[ChannelType.OnePager].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183789/Home%20Card%20Previews/ana3n9vlyteez5wypnub.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What is the primary focus of your one-pager?',
        generationType: ChannelType.OnePager,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.BattleCard]: BaseSchema.meta({
    type: ChannelType.BattleCard,
    icon: BattleCard,
    title: TextChannels[ChannelType.BattleCard].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183780/Home%20Card%20Previews/oxax7quj0rtamegkrqhn.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'Describe the competitors or topics for your battle card.',
        generationType: ChannelType.BattleCard,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.Testimonial]: BaseSchema.meta({
    type: ChannelType.Testimonial,
    icon: Testimonial,
    title: TextChannels[ChannelType.Testimonial].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183792/Home%20Card%20Previews/d06zss8d0xdx8nogeigb.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What details do you want to showcase in the testimonial?',
        generationType: ChannelType.Testimonial,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.ProductDemo]: BaseSchema.meta({
    type: ChannelType.ProductDemo,
    icon: ProductDemo,
    title: TextChannels[ChannelType.ProductDemo].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183789/Home%20Card%20Previews/zrapr3fkjoxlynntbvkm.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What are the key details or features of your product demo?',
        generationType: ChannelType.ProductDemo,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.PreCallChecklist]: BaseSchema.meta({
    type: ChannelType.PreCallChecklist,
    icon: PreCallChecklist,
    title: TextChannels[ChannelType.PreCallChecklist].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183790/Home%20Card%20Previews/uatr8ys6uwqu7wd8oubv.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'Describe your product and any key details of your pre-call checklist.',
        generationType: ChannelType.PreCallChecklist,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.CallScript]: BaseSchema.meta({
    type: ChannelType.CallScript,
    icon: CallScript,
    title: TextChannels[ChannelType.CallScript].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183780/Home%20Card%20Previews/jih2sco68xz4lyjmq9tj.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'Describe your product and the main goal of your call script.',
        generationType: ChannelType.CallScript,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.Research]: BaseSchema.meta({
    type: ChannelType.Research,
    icon: Research,
    title: TextChannels[ChannelType.Research].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183789/Home%20Card%20Previews/bs7ifip9oz8zbuy6nfqc.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What is the focus of your research document?',
        generationType: ChannelType.Research,
      }),
      include_images: includeImages,
      seo_words: seoWords.meta({ inCollapsibleSection: true }),
    }),
  ) as FormSchema,
  [ChannelType.ReplyTemplates]: BaseSchema.meta({
    type: ChannelType.ReplyTemplates,
    icon: ReplyTemplates,
    title: TextChannels[ChannelType.ReplyTemplates].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183791/Home%20Card%20Previews/b7yoiaa30ffluf8jw2iu.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'Provide the question or topic you are replying to and any details you would like included.',
        generationType: ChannelType.ReplyTemplates,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.HelpDocs]: BaseSchema.meta({
    type: ChannelType.HelpDocs,
    icon: HelpDocs,
    title: TextChannels[ChannelType.HelpDocs].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183787/Home%20Card%20Previews/mhscrips2yfhyuno62ta.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'Describe the product or feature your help document is for.',
        generationType: ChannelType.HelpDocs,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.ReturnPolicy]: BaseSchema.meta({
    type: ChannelType.ReturnPolicy,
    icon: ReturnPolicy,
    title: TextChannels[ChannelType.ReturnPolicy].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183790/Home%20Card%20Previews/y7nsascm9c99ujfnsvte.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'Describe any specific guidelines for your return policy.',
        generationType: ChannelType.ReturnPolicy,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.ShippingPolicy]: BaseSchema.meta({
    type: ChannelType.ShippingPolicy,
    icon: ShippingPolicy,
    title: TextChannels[ChannelType.ShippingPolicy].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183790/Home%20Card%20Previews/uleu7nt8uzehroxmugk4.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'Describe any specific details or considerations for your shipping policy.',
        generationType: ChannelType.ShippingPolicy,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.CareInstructions]: BaseSchema.meta({
    type: ChannelType.CareInstructions,
    icon: Care,
    title: TextChannels[ChannelType.CareInstructions].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183781/Home%20Card%20Previews/uy159swhhgon3vtc3i41.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'Describe the product or item and any care details.',
        generationType: ChannelType.CareInstructions,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.BillingAndPayments]: BaseSchema.meta({
    type: ChannelType.BillingAndPayments,
    icon: BillingAndPayments,
    title: TextChannels[ChannelType.BillingAndPayments].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183780/Home%20Card%20Previews/gnpwejz37yioh4uvsyqn.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'Describe your product and any topics or issues you want to address.',
        generationType: ChannelType.BillingAndPayments,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.PaymentConfirmation]: BaseSchema.meta({
    type: ChannelType.PaymentConfirmation,
    icon: PaymentConfirmation,
    title: TextChannels[ChannelType.PaymentConfirmation].name,
    previewUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1710183789/Home%20Card%20Previews/mrmsooffafrhisn7gsxq.png',
  }).concat(
    object({
      topic: topic.meta({
        label: 'What details would you like to include in your payment confirmation message?',
        generationType: ChannelType.PaymentConfirmation,
      }),
      include_images: includeImages,
    }),
  ) as FormSchema,
  [ChannelType.BlogPost]: BlogPostSchema,
  [ChannelType.NurtureEmailSequence]: NurtureEmailSequenceSchema, // goal instead of topic
  [ChannelType.PromotionalEmailSequence]: PromotionalEmailSequenceSchema, // goal instead of topic
  [ChannelType.CaseStudy]: CaseStudySchema, // case_study_outcomes instead of topic
  [ChannelType.YoutubeScript]: YoutubeScriptSchema,
  // TO REMOVE
  [GenerationType.Article]: BaseSchema.meta({ type: GenerationType.Article }) as FormSchema,
  [GenerationType.Generic]: BaseSchema.meta({
    type: GenerationType.Generic,
    icon: Blog,
    title: 'Custom Prompt',
    generationType: GenerationType.Generic,
  }) as FormSchema,
  [GenerationType.InlineGeneric]: BaseSchema.meta({
    type: GenerationType.InlineGeneric,
    icon: Blog,
    title: 'Custom Prompt',
    generationType: GenerationType.InlineGeneric,
  }) as FormSchema,
  [GenerationType.Image]: ImageSchema,
}

export const designSchemas: { [key in DesignChannel]: MultiStepSchema } = {
  [DesignChannel.InstagramPost]: {
    ...getBaseDesignSchemaWithCaption({
      generationType: DesignChannel.InstagramPost,
      textPostEntrypoint: ChannelType.InstagramPosts,
    }),
    meta: {
      type: DesignChannel.InstagramPost,
      channel: DesignChannel.InstagramPost,
      title: 'Instagram Post',
      icon: Instagram,
      previewUrl: '',
    },
  },
  [DesignChannel.InstagramReel]: {
    ...getBaseDesignSchemaWithCaption({ generationType: DesignChannel.InstagramReel }),
    meta: {
      type: DesignChannel.InstagramReel,
      channel: DesignChannel.InstagramReel,
      title: 'Instagram Reel',
      icon: Instagram,
      previewUrl: '',
    },
  },
  [DesignChannel.InstagramStory]: {
    ...BaseDesignSchemaWithoutCaption({ generationType: DesignChannel.InstagramStory }),
    meta: {
      type: DesignChannel.InstagramStory,
      channel: DesignChannel.InstagramStory,
      title: 'Instagram Story',
      icon: Instagram,
      previewUrl: '',
    },
  },
  [DesignChannel.FacebookStory]: {
    ...BaseDesignSchemaWithoutCaption({ generationType: DesignChannel.FacebookStory }),
    meta: {
      type: DesignChannel.FacebookStory,
      channel: DesignChannel.FacebookStory,
      title: 'Facebook Story',
      icon: Facebook,
      previewUrl: '',
    },
  },
  [DesignChannel.TikTokPost]: {
    ...getBaseDesignSchemaWithCaption({ generationType: DesignChannel.TikTokPost }),
    meta: {
      type: DesignChannel.TikTokPost,
      channel: DesignChannel.TikTokPost,
      title: 'TikTok Post',
      icon: TikTok,
      previewUrl: '',
    },
  },
  [DesignChannel.TikTokThumbnail]: {
    ...BaseDesignSchemaWithoutCaption({ generationType: DesignChannel.TikTokThumbnail }),
    meta: {
      type: DesignChannel.TikTokThumbnail,
      channel: DesignChannel.TikTokThumbnail,
      title: 'TikTok Thumbnail',
      icon: TikTok,
      previewUrl: '',
    },
  },
  [DesignChannel.YoutubeShort]: {
    ...getBaseDesignSchemaWithCaption({ generationType: DesignChannel.YoutubeShort }),
    meta: {
      type: DesignChannel.YoutubeShort,
      channel: DesignChannel.YoutubeShort,
      title: 'YouTube Short',
      icon: YouTube,
      previewUrl: '',
    },
  },
  [DesignChannel.YoutubeChannelArt]: {
    ...BaseDesignSchemaWithoutCaption({ generationType: DesignChannel.YoutubeChannelArt }),
    meta: {
      type: DesignChannel.YoutubeChannelArt,
      channel: DesignChannel.YoutubeChannelArt,
      title: 'Youtube Channel Art',
      icon: YouTube,
      previewUrl: '',
    },
  },
  [DesignChannel.YoutubeProfilePicture]: {
    ...BaseMarketplaceDesignSchema(),
    meta: {
      type: DesignChannel.YoutubeProfilePicture,
      channel: DesignChannel.YoutubeProfilePicture,
      title: 'Youtube Profile Picture',
      icon: YouTube,
      previewUrl: '',
    },
  },
  [DesignChannel.FacebookCover]: {
    ...BaseDesignSchemaWithoutCaption({ generationType: DesignChannel.FacebookCover }),
    meta: {
      type: DesignChannel.FacebookCover,
      channel: DesignChannel.FacebookCover,
      title: 'Facebook Cover',
      icon: Facebook,
      previewUrl: '',
    },
  },
  [DesignChannel.FacebookPost]: {
    ...getBaseDesignSchemaWithCaption({
      generationType: DesignChannel.FacebookPost,
      textPostEntrypoint: ChannelType.FacebookPost,
    }),
    meta: {
      type: DesignChannel.FacebookPost,
      channel: DesignChannel.FacebookPost,
      title: 'Facebook Post',
      icon: Facebook,
      previewUrl: '',
    },
  },
  [DesignChannel.FacebookMarketplace]: {
    ...BaseMarketplaceDesignSchema(),
    meta: {
      type: DesignChannel.FacebookMarketplace,
      channel: DesignChannel.FacebookMarketplace,
      title: 'Facebook Marketplace',
      icon: Facebook,
      previewUrl: '',
    },
  },
  [DesignChannel.FacebookProfilePicture]: {
    ...BaseMarketplaceDesignSchema(),
    meta: {
      type: DesignChannel.FacebookProfilePicture,
      channel: DesignChannel.FacebookProfilePicture,
      title: 'Facebook Profile Picture',
      icon: Facebook,
      previewUrl: '',
    },
  },
  [DesignChannel.LinkedInBanner]: {
    ...BaseDesignSchemaWithoutCaption({ generationType: DesignChannel.LinkedInBanner }),
    meta: {
      type: DesignChannel.LinkedInBanner,
      channel: DesignChannel.LinkedInBanner,
      title: 'LinkedIn Banner',
      icon: LinkedIn,
      previewUrl: '',
    },
  },
  [DesignChannel.LinkedInProfilePicture]: {
    ...BaseMarketplaceDesignSchema(),
    meta: {
      type: DesignChannel.LinkedInProfilePicture,
      channel: DesignChannel.LinkedInProfilePicture,
      title: 'LinkedIn Profile Picture',
      icon: LinkedIn,
      previewUrl: '',
    },
  },
  [DesignChannel.LinkedInPost]: {
    ...getBaseDesignSchemaWithCaption({
      generationType: DesignChannel.LinkedInPost,
      textPostEntrypoint: ChannelType.LinkedInPosts,
    }),
    meta: {
      type: DesignChannel.LinkedInPost,
      channel: DesignChannel.LinkedInPost,
      title: 'LinkedIn Post',
      icon: LinkedIn,
      previewUrl: '',
    },
  },
  [DesignChannel.TwitterProfilePicture]: {
    ...BaseMarketplaceDesignSchema(),
    meta: {
      type: DesignChannel.TwitterProfilePicture,
      channel: DesignChannel.TwitterProfilePicture,
      title: 'X/Twitter Profile Picture',
      icon: Twitter,
      previewUrl: '',
    },
  },
  [DesignChannel.TwitterCoverImage]: {
    ...BaseDesignSchemaWithoutCaption({ generationType: DesignChannel.TwitterCoverImage }),
    meta: {
      type: DesignChannel.TwitterCoverImage,
      channel: DesignChannel.TwitterCoverImage,
      title: 'X/Twitter Cover Image',
      icon: Twitter,
      previewUrl: '',
    },
  },
  [DesignChannel.WhatsAppSticker]: {
    ...BaseMarketplaceDesignSchema(),
    meta: {
      type: DesignChannel.WhatsAppSticker,
      channel: DesignChannel.WhatsAppSticker,
      title: 'WhatsApp Sticker',
      icon: WhatsApp,
      previewUrl: '',
    },
  },
  [DesignChannel.Pinterest]: {
    ...BaseMarketplaceDesignSchema(),
    meta: {
      type: DesignChannel.Pinterest,
      channel: DesignChannel.Pinterest,
      title: 'Pinterest Post',
      icon: Pinterest,
      previewUrl: '',
    },
  },
  [DesignChannel.Ebay]: {
    ...BaseMarketplaceDesignSchema(),
    meta: {
      type: DesignChannel.Ebay,
      channel: DesignChannel.Ebay,
      title: 'eBay Product Visuals',
      icon: Ebay,
      previewUrl: '',
    },
  },
  [DesignChannel.Poshmark]: {
    ...BaseMarketplaceDesignSchema(),
    meta: {
      type: DesignChannel.Poshmark,
      channel: DesignChannel.Poshmark,
      title: 'Poshmark Product Visuals',
      icon: Poshmark,
      previewUrl: '',
    },
  },
  [DesignChannel.Depop]: {
    ...BaseMarketplaceDesignSchema(),
    meta: {
      type: DesignChannel.Depop,
      channel: DesignChannel.Depop,
      title: 'Depop Product Visuals',
      icon: Depop,
      previewUrl: '',
    },
  },
  [DesignChannel.Mercari]: {
    ...BaseMarketplaceDesignSchema(),
    meta: {
      type: DesignChannel.Mercari,
      channel: DesignChannel.Mercari,
      title: 'Mercari Product Visuals',
      icon: Mercari,
      previewUrl: '',
    },
  },
  [DesignChannel.MercadoLibre]: {
    ...BaseMarketplaceDesignSchema(),
    meta: {
      type: DesignChannel.MercadoLibre,
      channel: DesignChannel.MercadoLibre,
      title: 'MercadoLibre Product Visuals',
      icon: Mercadolibre,
      previewUrl: '',
    },
  },
  [DesignChannel.Shopee]: {
    ...BaseMarketplaceDesignSchema(),
    meta: {
      type: DesignChannel.Shopee,
      channel: DesignChannel.Shopee,
      title: 'Shopee Product Visuals',
      icon: Shopee,
      previewUrl: '',
    },
  },
  [DesignChannel.Lazada]: {
    ...BaseMarketplaceDesignSchema(),
    meta: {
      type: DesignChannel.Lazada,
      channel: DesignChannel.Lazada,
      title: 'Lazada Product Visuals',
      icon: Lazada,
      previewUrl: '',
    },
  },
  [DesignChannel.Etsy]: {
    ...BaseMarketplaceDesignSchema(),
    meta: {
      type: DesignChannel.Etsy,
      channel: DesignChannel.Etsy,
      title: 'Etsy Product Visuals',
      icon: Etsy,
      previewUrl: '',
    },
  },
  [DesignChannel.Vinted]: {
    ...BaseMarketplaceDesignSchema(),
    meta: {
      type: DesignChannel.Vinted,
      channel: DesignChannel.Vinted,
      title: 'Vinted Product Visuals',
      icon: Vinted,
      previewUrl: '',
    },
  },
  [DesignChannel.Amazon]: {
    ...BaseMarketplaceDesignSchema(),
    meta: {
      type: DesignChannel.Amazon,
      channel: DesignChannel.Amazon,
      title: 'Amazon Product Visuals',
      icon: Amazon,
      previewUrl: '',
    },
  },
  [DesignChannel.Shopify]: {
    ...BaseMarketplaceDesignSchema(),
    meta: {
      type: DesignChannel.Shopify,
      channel: DesignChannel.Shopify,
      title: 'Shopify Product Visuals',
      icon: Shopify,
      previewUrl: '',
    },
  },
  [DesignChannel.TwitterPost]: {
    ...getBaseDesignSchemaWithCaption({
      generationType: DesignChannel.TwitterPost,
      textPostEntrypoint: ChannelType.Tweets,
    }),
    meta: {
      type: DesignChannel.TwitterPost,
      channel: DesignChannel.TwitterPost,
      title: 'X/Twitter Post',
      icon: Twitter,
      previewUrl: '',
    },
  },
  [DesignChannel.Post]: {
    ...BaseDesignSchemaWithoutCaption({ generationType: DesignChannel.Post }),
    meta: {
      type: DesignChannel.Post,
      channel: DesignChannel.Post,
      title: 'Post',
      icon: Design,
      previewUrl: '',
    },
  },
  [DesignChannel.GoogleMyBusinessPost]: {
    ...getBaseDesignSchemaWithCaption({
      generationType: DesignChannel.GoogleMyBusinessPost,
    }),
    meta: {
      type: DesignChannel.GoogleMyBusinessPost,
      channel: DesignChannel.GoogleMyBusinessPost,
      title: 'Google Business Profile Post',
      icon: Google,
      previewUrl: '',
    },
  },
  [DesignChannel.MetaAds]: {
    ...BaseDesignSchemaWithoutCaption({ generationType: DesignChannel.MetaAds }),
    meta: {
      type: DesignChannel.MetaAds,
      channel: DesignChannel.MetaAds,
      title: 'Meta Ad Creative',
      icon: MetaBrand,
      previewUrl: '',
    },
  },
}

export const videoSchemaOverrides: Partial<{ [key in DesignChannel]: MultiStepSchema }> = {
  [DesignChannel.InstagramReel]: {
    ...BaseVideoDesignSchema({ designChannel: DesignChannel.InstagramReel }),
    meta: {
      type: DesignChannel.InstagramReel,
      channel: DesignChannel.InstagramReel,
      title: 'Instagram Reel',
      icon: Instagram,
      previewUrl: '',
      isVideo: true,
    },
  },
  [DesignChannel.TikTokPost]: {
    ...BaseVideoDesignSchema({ designChannel: DesignChannel.TikTokPost }),
    meta: {
      type: DesignChannel.TikTokPost,
      channel: DesignChannel.TikTokPost,
      title: 'TikTok Post',
      icon: TikTok,
      previewUrl: '',
      isVideo: true,
    },
  },
  [DesignChannel.YoutubeShort]: {
    ...BaseVideoDesignSchema({ designChannel: DesignChannel.YoutubeShort }),
    meta: {
      type: DesignChannel.YoutubeShort,
      channel: DesignChannel.YoutubeShort,
      title: 'YouTube Short',
      icon: YouTube,
      previewUrl: '',
      isVideo: true,
    },
  },
}
